/**
 * Team → At risk (prototype VIEWS.atrisk, docs/prototype.html 3669–3684): early warnings, worst first.
 * Four stats (hidden inside the Team hub by the prototype CSS, as there), the Everything / Waiting / Late /
 * Quiet or falling pills with counts, and one row per item: client logo, title, client · owner (or "No owner") ·
 * kind chip, the reason in bold and the next step, then "Open" (or "Open brief" for a quiet client) and
 * "Message <owner>" (the same WhatsApp modal as Timesheets, prototype `waModal`).
 *
 * Data: GET /at-risk (`team.read`) — ranking, reasons, next step and the summary all come from the server.
 * "Open" follows the item's own `subject.route`: a `?task=` / `?request=` link opens its panel over this page.
 */
import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { RISK_KIND_LABEL, type RiskItem, type RiskKind, type UserRef } from '@amplispot/contracts';
import { firstName } from '../../lib/format';
import { useSession } from '../../lib/session';
import { useAtRisk } from '../../lib/targets';
import { Empty, ErrorState, Loading, Pills } from '../../ui/kit';
import { ClientLogo } from '../clients/parts';
import { Stat } from '../timesheets/TimesheetReport';
import { WhatsAppModal } from '../timesheets/TsModals';

/** Prototype RCAT: the chip tone per kind. */
const TONE: Record<RiskKind, string> = { wait: 'info', late: 'cold', quiet: 'warm' };

export function AtRisk() {
  const { me } = useSession();
  const [sp, setSp] = useSearchParams();
  const loc = useLocation();
  const nav = useNavigate();
  const k = sp.get('kind');
  const kind: RiskKind | 'all' = k === 'wait' || k === 'late' || k === 'quiet' ? k : 'all';
  const q = useAtRisk(kind);
  const [wa, setWa] = useState<UserRef | null>(null);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const pages = q.data?.pages ?? [];
  const items = pages.flatMap((p) => p.data);
  const S = pages[0]?.summary ?? { total: 0, wait: 0, late: 0, quiet: 0, high_priority_clients: 0 };

  const open = (i: RiskItem) => {
    const u = new URL(i.subject.route, window.location.origin);
    // A panel link (?task=, ?request=, …) on the same or no path opens over this page; anything else navigates.
    if (u.pathname === loc.pathname || u.pathname === '/') {
      setSp((p) => {
        const n = new URLSearchParams(p);
        u.searchParams.forEach((v, key) => n.set(key, v));
        return n;
      });
    } else nav(u.pathname + u.search);
  };

  return (
    <>
      <div className="stats">
        <Stat k="Waiting on someone else" v={S.wait} s="blocked tasks" cls={S.wait ? 'amber' : 'green'} />
        <Stat k="Running late" v={S.late} s="tasks and requests" cls={S.late ? 'red' : 'green'} />
        <Stat k="Going quiet or falling" v={S.quiet} s="clients" cls={S.quiet ? 'amber' : 'green'} />
        <Stat k="High priority clients involved" v={S.high_priority_clients} cls={S.high_priority_clients ? 'red' : 'green'} />
      </div>
      <div className="bar">
        <Pills<RiskKind | 'all'>
          options={[
            ['all', 'Everything', S.total],
            ['wait', 'Waiting', S.wait],
            ['late', 'Late', S.late],
            ['quiet', 'Quiet or falling', S.quiet],
          ]}
          value={kind}
          onChange={(v) =>
            setSp((p) => {
              const n = new URLSearchParams(p);
              if (v === 'all') n.delete('kind');
              else n.set('kind', v);
              return n;
            })
          }
        />
      </div>
      <div className="panel">
        {items.length ? (
          <>
            {items.map((i) => (
              <div key={i.key} className="li" style={{ alignItems: 'flex-start', gap: 12 }}>
                {i.client ? <ClientLogo id={i.client.id} name={i.client.name} size={34} /> : null}
                <div className="b">
                  <div className="t">{i.subject.label}</div>
                  <div className="m">
                    <span>{i.client ? i.client.name : 'Internal'}</span>
                    {i.owner ? <span>{firstName(i.owner.name)}</span> : <span style={{ color: 'var(--cold)', fontWeight: 600 }}>No owner</span>}
                    <span className={`chip ${TONE[i.kind]}`}>{RISK_KIND_LABEL[i.kind]}</span>
                  </div>
                  <div style={{ fontSize: 13, marginTop: 6 }}>
                    {/* `next` already names the suggested assignee for a late task (prototype riskItems). */}
                    <b>{i.reason.replace(/\.$/, '')}.</b> <span className="sub">{i.next}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button className="btn sm" onClick={() => open(i)}>
                    {i.kind === 'quiet' ? 'Open brief' : 'Open'}
                  </button>
                  {i.owner && i.owner.id !== me.user.id ? (
                    <button className="btn sm pri" onClick={() => setWa(i.owner)}>
                      Message {firstName(i.owner.name)}
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
            {q.hasNextPage ? (
              <div className="li" style={{ justifyContent: 'center' }}>
                <button className="btn sm ghost" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
                  {q.isFetchingNextPage ? 'Loading…' : 'Show more'}
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <Empty title="Nothing at risk">Everything is moving.</Empty>
        )}
      </div>
      {wa ? <WhatsAppModal user={wa} missingDay={null} onClose={() => setWa(null)} /> : null}
    </>
  );
}
